import { create } from 'zustand';
import { temporal } from 'zundo';

const initialState = {
  scene: null,
  selectedActorId: null,
  selectedEventId: null,
  selectedEventIndex: 0,
  isDirty: false,
  coordSystem: { width: 100, height: 53.3, unit: 'yards' },
  formations: [],
  actorRoles: [],
  sceneTypes: [],
  timelineVocab: [],
};

export const useSceneStore = create(
  temporal(
    (set, get) => ({
      ...initialState,

      loadScene: (scene) => {
        set({
          scene,
          selectedEventIndex: 0,
          selectedEventId: scene?.timeline?.[0]?.event_id ?? null,
          selectedActorId: scene?.actors?.[0]?.actor_id ?? null,
          isDirty: false,
        });
        // Clear undo/redo history *after* loading. Otherwise zundo
        // remembers the scene=null pre-load state, and a single click of
        // "Undo" rolls the editor back to a blank screen — coaches saw
        // a black canvas and had to reload the page. By clearing here
        // we make ⌘Z bounded by the coach's first edit, never the load.
        try {
          useSceneStore.temporal.getState().clear();
        } catch (_) { /* zundo not yet wired */ }
      },

      setMeta: (patch) =>
        set((s) => ({
          scene: s.scene ? { ...s.scene, ...patch } : s.scene,
          isDirty: true,
        })),

      setDomainMetadata: (patch) =>
        set((s) => ({
          scene: s.scene
            ? { ...s.scene, domain_metadata: { ...(s.scene.domain_metadata || {}), ...patch } }
            : s.scene,
          isDirty: true,
        })),

      selectActor: (actorId) => set({ selectedActorId: actorId }),
      selectEvent: (eventIndex) => {
        const ev = get().scene?.timeline?.[eventIndex];
        set({ selectedEventIndex: eventIndex, selectedEventId: ev?.event_id ?? null });
      },

      moveActor: (actorId, x, y) =>
        set((s) => {
          if (!s.scene) return s;
          const eventIdx = s.selectedEventIndex;
          const newTimeline = s.scene.timeline.map((ev, i) => {
            if (i !== eventIdx) return ev;
            const states = { ...(ev.actor_states || {}) };
            states[actorId] = {
              ...(states[actorId] || {}),
              position: { x, y, z: 0 },
            };
            return { ...ev, actor_states: states };
          });
          return { scene: { ...s.scene, timeline: newTimeline }, isDirty: true };
        }),

      addActor: (actor) =>
        set((s) => {
          if (!s.scene) return s;
          const actors = [...(s.scene.actors || []), actor];
          const newTimeline = s.scene.timeline.map((ev, i) => {
            if (i !== 0) return ev;
            return {
              ...ev,
              actor_states: {
                ...(ev.actor_states || {}),
                [actor.actor_id]: actor.initial_state,
              },
            };
          });
          return { scene: { ...s.scene, actors, timeline: newTimeline }, isDirty: true };
        }),

      removeActor: (actorId) =>
        set((s) => {
          if (!s.scene) return s;
          const actors = (s.scene.actors || []).filter((a) => a.actor_id !== actorId);
          const newTimeline = s.scene.timeline.map((ev) => {
            const states = { ...(ev.actor_states || {}) };
            delete states[actorId];
            return { ...ev, actor_states: states };
          });
          return {
            scene: { ...s.scene, actors, timeline: newTimeline },
            isDirty: true,
            selectedActorId: s.selectedActorId === actorId ? null : s.selectedActorId,
          };
        }),

      addEvent: (afterIndex = null) =>
        set((s) => {
          if (!s.scene) return s;
          const timeline = [...s.scene.timeline];
          const at = afterIndex == null ? timeline.length : afterIndex + 1;
          const prev = timeline[at - 1] || timeline[0] || { time_ms: 0, actor_states: {} };
          const newEvent = {
            event_id: `event_${Date.now().toString(36)}`,
            time_ms: (prev.time_ms || 0) + 1000,
            display_name: 'New event',
            actor_states: JSON.parse(JSON.stringify(prev.actor_states || {})),
            coaching_script: null,
            audio_url: null,
            annotations: [],
            metadata: {},
          };
          timeline.splice(at, 0, newEvent);
          return {
            scene: { ...s.scene, timeline },
            isDirty: true,
            selectedEventIndex: at,
            selectedEventId: newEvent.event_id,
          };
        }),

      removeEvent: (index) =>
        set((s) => {
          if (!s.scene || s.scene.timeline.length <= 1) return s;
          const timeline = s.scene.timeline.filter((_, i) => i !== index);
          const newIdx = Math.min(s.selectedEventIndex, timeline.length - 1);
          return {
            scene: { ...s.scene, timeline },
            selectedEventIndex: newIdx,
            selectedEventId: timeline[newIdx]?.event_id ?? null,
            isDirty: true,
          };
        }),

      updateEvent: (index, patch) =>
        set((s) => {
          if (!s.scene) return s;
          const timeline = s.scene.timeline.map((ev, i) => (i === index ? { ...ev, ...patch } : ev));
          return { scene: { ...s.scene, timeline }, isDirty: true };
        }),

      setTrigger: (trigger) =>
        set((s) => {
          if (!s.scene) return s;
          const triggers = [...(s.scene.triggers || [])];
          const idx = triggers.findIndex((t) => t.trigger_id === trigger.trigger_id);
          if (idx >= 0) triggers[idx] = trigger;
          else triggers.push(trigger);
          return { scene: { ...s.scene, triggers }, isDirty: true };
        }),

      removeTrigger: (triggerId) =>
        set((s) => {
          if (!s.scene) return s;
          const triggers = (s.scene.triggers || []).filter((t) => t.trigger_id !== triggerId);
          return { scene: { ...s.scene, triggers }, isDirty: true };
        }),

      markClean: () => set({ isDirty: false }),

      setPackData: ({ coordSystem, formations, actorRoles, sceneTypes, timelineVocab }) =>
        set((s) => ({
          coordSystem: coordSystem || s.coordSystem,
          formations: formations || s.formations,
          actorRoles: actorRoles || s.actorRoles,
          sceneTypes: sceneTypes || s.sceneTypes,
          timelineVocab: timelineVocab || s.timelineVocab,
        })),
    }),
    {
      limit: 50,
      // Only track the actual scene document in undo/redo history.
      // Without `partialize`, zundo also captured selectedEventIndex,
      // selectedActorId, formations, sceneTypes, etc. — so clicking on
      // a different event would be undoable, and undo could wipe the
      // pack-loaded reference data alongside the scene. Restricting to
      // ``scene`` keeps Cmd-Z meaning "undo my last edit to the play".
      partialize: (state) => ({ scene: state.scene }),
      equality: (a, b) => a.scene === b.scene,
    }
  )
);
